import React, { memo, useMemo } from 'react';

import SvgSprite from '@/components/atomic/atoms/data-display/icons/icon-sprite';
import IconNames from '@/components/atomic/atoms/data-display/icons/symbol/enum';
import FlexContainer from '@/components/atomic/atoms/layout/flex-container/styled';
import FlexItem from '@/components/atomic/atoms/layout/flex-item';
import TopNavItemAnchor from '@/components/atomic/molecules/navigation/top-nav-items/item/anchor';
import Styled from '@/components/atomic/molecules/navigation/top-nav-items/item/styled';
import type { TopNavigationItemRendererProps } from '@/components/atomic/molecules/navigation/top-nav-items/item/typings';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import withExposer from '@/hocs/with-exposer';
import { useTheme } from '@emotion/react';

const TopNavItemRendererComponent: React.FC<TopNavigationItemRendererProps> = ({
	id,
	ariaLabel,
	label,
	link,
	fragmentVariationPath,
	dropdownSegment,
	left,
	right,
	$variant,
	$hasLabel,
}) => {
	const { size } = useTheme();

	const utilityVariantAndDesktop = useMemo(
		() => (
			<FlexContainer $height={'100%'} $gap={12}>
				{left}
				{right}
			</FlexContainer>
		),
		[left, right],
	);

	const primaryVariant = useMemo(
		() => (
			<FlexContainer $height={'100%'}>
				<FlexItem $flex={1}>
					<FlexContainer $height={'100%'} $gap={12}>
						{left}
						{right}
					</FlexContainer>
				</FlexItem>
				<FlexItem $alignItems='center' $paddingLeft={24}>
					<SvgSprite
						icon={IconNames.CHEVRON_RIGHT}
						$width={10}
						$height={10}
					/>
				</FlexItem>
			</FlexContainer>
		),
		[left, right],
	);

	return (
		<Styled
			id={`naas-nav-item-${id}`}
			$variant={$variant}
			$hasLabel={$hasLabel ?? !!label}
		>
			<TopNavItemAnchor
				link={link}
				ariaLabel={ariaLabel}
				fragmentVariationPath={fragmentVariationPath}
				label={label}
				id={id}
				dropdownSegment={dropdownSegment}
				$variant={$variant}
			>
				<ViewportRender
					node={
						$variant === 'primary'
							? primaryVariant
							: utilityVariantAndDesktop
					}
					$breakpoints={{
						[size.laptop]: {
							node: utilityVariantAndDesktop,
						},
					}}
				/>
			</TopNavItemAnchor>
		</Styled>
	);
};

const TopNavItemRenderer = memo(
	withExposer(TopNavItemRendererComponent, 'topNavItemRenderer'),
);

export default TopNavItemRenderer;
